<template>
  <section
    class="map-section"
    :style="{ '--transation-x': mapTranslationX + 'px' }"
  >
    <MapTop
      :title="title"
      :cta="cta"
      :dropdownPlaceholder="dropdownPlaceholder"
      :SDGLegend="SDGLegend"
      :selectedSDG="selectedSDG"
      :smallscreen="smallscreen"
      @onSDGSelect="filterSDGs"
    />

    <div class="map-section__map-wrapper" ref="mapEl">
      <MapViz class="map-section__map" />
    </div>

    <MapTooltip
      v-if="hoveredCountryCode || selectedCountryCode"
      class="map-section__tooltip"
      :position="tooltipPosition"
      :country="$t(`countryMapData.${hoveredCountryCode}.name`)"
    />

    <Transition name="slide-left">
      <SDGPanel
        v-if="selectedCountryCode"
        :country="countryData[selectedCountryCode].name"
        :sectors="countryData[selectedCountryCode].sectors"
        :columnLabels="$tm('general.labels.SDGLegend')"
        :text="countryText"
        :smallscreen="smallscreen"
        @onClose="showSidePanel($event, null)"
      />
    </Transition>
  </section>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import useTvaMq from "@/plugins/tvaMq.js";

import MapViz from "./MapViz.vue";
import MapTop from "./MapTop.vue";
import MapTooltip from "./MapTooltip.vue";
import SDGPanel from "./SDGPanel.vue";

const props = defineProps({
  title: String,
  cta: String,
  SDGLegend: Object,
  countryCodes: Object,
  dropdownPlaceholder: String,
});

const { tm } = useI18n();

const { $tvaMq } = useTvaMq();
const smallscreen = computed(() => ["tablet", "mobile"].includes($tvaMq.value));

// const mapSectionEl = ref(null);
const hoveredCountryCode = ref(null);
const selectedCountryCode = ref(null);
const tooltipPosition = ref({ x: 0, y: 0 });
const countryElements = ref([]);
const circleElements = ref([]);
const mapTranslationX = ref(0);
const selectedSDG = ref(null);
const mapEl = ref(null);
const codesWithSDG = ref([]);

const countryData = computed(() => tm("countryMapData"));

const countryText = computed(() => {
  if (selectedCountryCode.value) {
    const countryModalContent = tm("countryModalData").find(
      (country) => country.countryCode === selectedCountryCode.value
    );
    return countryModalContent?.cardSubscript || "";
  }

  return "";
});

onMounted(() => {
  if (smallscreen.value && mapEl.value) {
    mapEl.value.scroll(window.innerWidth * 1.25, 0);
  }

  props.countryCodes.map((code) => {
    const countryEl = document.getElementById(code.toLowerCase());
    const circleEl = document.getElementById(code);

    if (countryEl) {
      makeElementInteractive(countryEl, code);

      countryEl.classList.add("country");
      countryElements.value.push(countryEl);
    }

    if (circleEl) {
      makeElementInteractive(circleEl, code);

      circleElements.value.push(circleEl);
    }
  });
});

function makeElementInteractive(el, code) {
  el.addEventListener("click", (e) => showSidePanel(e, code));

  el.addEventListener("mousemove", (event) => toggleTooltip(event, code));

  el.addEventListener("mouseleave", (event) => toggleTooltip(event, null));
}

function showSidePanel(mouseEvent, countryCode) {
  // If clicking on other country, don't open panel
  if (mouseEvent && ["circle", "path"].includes(mouseEvent.target.nodeName)) {
    mouseEvent.stopPropagation();
  }
  // If a country code has already been selected, only reset showing panel
  if (selectedCountryCode.value) {
    countryCode = null;
  }

  selectedCountryCode.value = countryCode;
  hoveredCountryCode.value = countryCode;

  circleElements.value.forEach((circle) => {
    if (countryCode && circle.id !== countryCode) {
      circle.classList.add("unselected");
    } else if (!countryCode) {
      circle.classList.remove("unselected");
    }
  });

  countryElements.value.forEach((country) => {
    if (countryCode && country.id.toUpperCase() !== countryCode) {
      country.classList.add("unselected");
    } else if (!countryCode) {
      country.classList.remove("unselected");
    }
  });

  // Make sure that clicked datapoint is always at fixed end from left side of screen
  if (mouseEvent && countryCode && !smallscreen.value) {
    const xOffset = mouseEvent.offsetX;
    mapTranslationX.value = -(xOffset - 100);
  } else {
    mapTranslationX.value = 0;
  }
}

function toggleTooltip(mouseEvent, countryCode) {
  if (!selectedCountryCode.value) {
    if (
      !codesWithSDG.value.length ||
      codesWithSDG.value.includes(countryCode)
    ) {
      hoveredCountryCode.value = countryCode;
    } else {
      hoveredCountryCode.value = null;
    }

    if (countryCode) {
      tooltipPosition.value = { x: mouseEvent.offsetX, y: mouseEvent.offsetY };
    }
  }
}

function filterSDGs(SDG, fromShowSidePanel = false) {
  if (!fromShowSidePanel) showSidePanel(null, null);

  // If clicked svg has already been selected, reset to normal state
  if (!SDG || (selectedSDG.value && selectedSDG.value.id === SDG.id)) {
    circleElements.value.forEach((circleEl) => {
      circleEl.style.fill = `#A42456`;
      circleEl.classList.remove("sdg-unselected");
    });

    selectedSDG.value = null;
    codesWithSDG.value = [];
  } else {
    codesWithSDG.value = Object.entries(countryData.value)
      // eslint-disable-next-line
      .filter(([_, data]) =>
        data.sectors.find((sector) => sector.id === SDG.id)
      )
      .map(([countryCode]) => countryCode);

    circleElements.value.forEach((circleEl) => {
      if (codesWithSDG.value.includes(circleEl.id)) {
        circleEl.style.fill = `var(--sviluppo-${SDG.id})`;
        circleEl.style.stroke = `var(--scala-grigi-gray-1)`;
        circleEl.classList.remove("sdg-unselected");
      } else {
        circleEl.classList.add("sdg-unselected");
        circleEl.style.fill = `var(--black-100)`;
        circleEl.style.stroke = "";
      }
    });

    countryElements.value.forEach((countryEl) => {
      if (codesWithSDG.value.includes(countryEl.id.toUpperCase())) {
        countryEl.classList.remove("sdg-unselected");
      } else {
        countryEl.classList.add("sdg-unselected");
      }
    });

    selectedSDG.value = SDG;
  }
}
</script>

<style lang="scss" scoped>
.map-section {
  position: relative;
  background: radial-gradient(
    56.36% 56.36% at 50% 52.79%,
    #bbd6dd 18.23%,
    #89b8c3 100%
  );
  min-height: toRem(620);
  height: calc(var(--vh) * 95);
  padding: 0 calc(var(--page-padding-h) * 0.75);

  .mobile &,
  .tablet & {
    padding: 0;

    &__map-wrapper {
      height: 100%;
      overflow: auto;
    }
  }

  &__map {
    .desktop &,
    .large & {
      width: 100%;
      transform: translateX(var(--transation-x));
      transition: transform 1s;
    }

    .mobile &,
    .tablet & {
      width: auto;
      height: 90%;
      transform: translateY(10%);
    }
  }
}
</style>
